import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-navy-600 text-white shadow-button hover:bg-navy-700 hover:shadow-button-hover focus-visible:ring-navy-500",
        destructive:
          "bg-red-600 text-white shadow-button hover:bg-red-700 hover:shadow-button-hover focus-visible:ring-red-500",
        outline:
          "border-2 border-navy-600 bg-transparent text-navy-600 hover:bg-navy-50 focus-visible:ring-navy-500",
        secondary:
          "bg-yellow-500 text-neutral-800 shadow-button hover:bg-yellow-600 hover:shadow-button-hover focus-visible:ring-yellow-500",
        ghost: "hover:bg-navy-50 hover:text-navy-600 focus-visible:ring-navy-500",
        link: "text-navy-600 underline-offset-4 hover:underline focus-visible:ring-navy-500",
        success:
          "bg-green-600 text-white shadow-button hover:bg-green-700 hover:shadow-button-hover focus-visible:ring-green-500",
      },
      size: {
        default: "h-11 px-6 py-3",
        sm: "h-9 rounded-md px-4 py-2 text-xs",
        lg: "h-12 rounded-lg px-8 py-3 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
